import { Injectable } from '@angular/core';
import { BaseApi } from '../_base-api';
import {
  AttributeDetail,
  AttributeEntity,
  BrandEntity,
  CategoryItem,
  ProductDropdownItem,
  ProductEntity,
  ProductItem,

  RelatedProductItem,

  VariantUpdate,
} from '@models';
import { map, Observable, of } from 'rxjs';
import { DataGetFn, DataGetOptions, ItemRecord } from '@controladad/ng-base';
import { GetApiAdapter } from '../../adapters';
import { IProductApiService } from './product-api.interface';
import { joinNamesFn } from '../../helpers';

@Injectable({
  providedIn: 'root',
})
export class VendorProductsApiService extends BaseApi<ProductEntity> implements IProductApiService {
  constructor() {
    super('/VendorPanel/Product', {
      get: '/VendorPanel/VendorGetProducts',
    });
  }

  override get(id: number): Observable<ProductEntity> {
    return http$.get<ProductEntity>(`/VendorPanel/VendorGetProduct?ProductId=${id}`);
  }

  // @ts-ignore
  override getAdapter(options?: DataGetOptions<ProductItem>) {
    return GetApiAdapter('/VendorPanel/VendorGetProducts', options);
  }

  // @ts-ignore
  override create(model: any) {
    return http$.post('/VendorPanel/VendorAddProduct', model);
  }

  // @ts-ignore
  override update(model: any) {
    return http$.put('/VendorPanel/VendorEditProduct', model);
  }

  // @ts-ignore
  override delete(ids: number[]) {
    return http$.delete('/VendorPanel/VendorDeleteProducts', { body: { productIds: ids } });
  }

  changeStatus(enable: boolean, ids: number[]) {
    return http$.patch('/VendorPanel/VendorChangeProductState', { ids, state: enable ? 1 : 0 });
  }

  changeApproval(): Observable<object> {
    return of();
  }

  getAllVendors(): Observable<ItemRecord<number>[]> {
    return of([]);
  }

  addVariants(data: { productId: number; variants: VariantUpdate[] }) {
    return http$.post('/VendorPanel/VendorCreateVariant', data);
  }

  deleteVariants(variantIds: number[]) {
    return http$.delete('/VendorPanel/VendorDeleteVariants', { body: { variantIds } });
  }

  getBrandItems(): Observable<ItemRecord<number>[]> {
    return super
      .getAll<BrandEntity>(undefined, '/VendorPanel/VendorGetBrandList')
      .pipe(map((brands) => brands.map((b) => ({ label: b.name.english, value: b.id }))));
  }

  getAttributeItemRecords(categoryIds: number[]) {
    const nameFn = joinNamesFn(' - ');
    const ids = new URLSearchParams();
    categoryIds.forEach((id) => ids.append('CategoryIds', id.toString()));

    return this.getAll<AttributeEntity>(undefined, `/VendorPanel/VendorGetAttributes?${ids}`).pipe(
      map((item) => item.map((record) => ({ label: nameFn(record.name), value: record.id }))),
    );
  }

  getAttributeTermItemRecords(id: number) {
    return http$
      .get<AttributeDetail>(`/VendorPanel/VendorGetAttribute?AttributeId=${id}`)
      .pipe(map((value) => value.values.map((term) => ({ value: term.id, label: joinNamesFn(' - ')(term.value) }))));
  }

  getCategories(): Observable<CategoryItem[]> {
    return super.getAll<CategoryItem>(undefined, '/VendorPanel/VendorGetCategoryList');
  }

  getRelatedProductItems(): Observable<RelatedProductItem[]> {
    return super
      .getAll<RelatedProductItem>(undefined, '/VendorPanel/VendorGetProducts');
  }

  getProductNamesAdapter(options?: DataGetOptions<ProductDropdownItem>): DataGetFn<ProductDropdownItem> {
    return GetApiAdapter('/VendorPanel/VendorGetProductNames', options);
  }

  getProductDropdownItems(productIds: number[]): Observable<ProductDropdownItem[]> {
    if (productIds.length === 0) {
      return of([]);
    }

    return this.getAll<ProductDropdownItem>({
      filters: [{ key: 'ProductIds', value: productIds }],
    }, `/VendorPanel/VendorGetProductNames`);
  }

  exportProducts(): Observable<Blob> {
    throw new Error('Export is not implemented for vendor panel');
  }
}
